import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faCheck } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';

/**
 * Company type selection card — Radix-inspired surface that mirrors the
 * CgDialog / v2 form visual language (rounded-2xl, soft slate borders,
 * subtle hover-shadow, focus-visible ring with primaryColor/40).
 */
const CompanyTypeCard = ({ title, icon, description, highlights, onClick, isLoading }) => {
  const { t } = useTranslation('onboarding');
  const [isHovered, setIsHovered] = useState(false);
  const showHighlight = isHovered && !isLoading;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isLoading}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsHovered(true)}
      onBlur={() => setIsHovered(false)}
      aria-label={t('typeSelect.selectCompanyType', 'Select {{title}} company type', { title })}
      style={{
        borderColor: showHighlight ? 'rgb(var(--primary-color))' : undefined,
        boxShadow: showHighlight
          ? '0 0 0 2px rgb(var(--primary-color) / 0.5), 0 10px 15px -3px rgb(0 0 0 / 0.1)'
          : undefined,
        transform: showHighlight ? 'translateY(-2px)' : undefined,
      }}
      className="group relative flex flex-col text-left w-full p-6 bg-white border border-slate-200 rounded-2xl
        transition-all duration-200
        focus:outline-none
        disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <div className="flex items-center justify-between mb-5">
        <div
          className="w-12 h-12 rounded-xl bg-primaryColor/10 text-primaryColor flex items-center justify-center
          transition-colors duration-200 group-hover:bg-primaryColor group-hover:text-white"
        >
          <FontAwesomeIcon
            icon={icon}
            className="text-xl"
            aria-hidden="true"
          />
        </div>
        <span className="text-slate-300 group-hover:text-primaryColor transition-colors">
          <FontAwesomeIcon
            icon={faArrowRight}
            className="text-sm"
          />
        </span>
      </div>

      <h3 className="text-lg font-semibold text-slate-900 mb-1.5">{title}</h3>
      <p className="text-sm text-slate-600 leading-relaxed mb-4">{description}</p>

      {highlights?.length ? (
        <ul className="mt-auto space-y-2.5 pt-4 border-t border-slate-100">
          {highlights.map((item) => (
            <li
              key={item}
              className="flex items-start gap-2.5 text-sm text-slate-700 leading-snug"
            >
              <span
                aria-hidden="true"
                className="mt-0.5 w-4 h-4 rounded-full bg-primaryColor/10 text-primaryColor flex items-center justify-center shrink-0"
              >
                <FontAwesomeIcon
                  icon={faCheck}
                  className="text-[9px]"
                />
              </span>
              {item}
            </li>
          ))}
        </ul>
      ) : null}

      <span
        aria-hidden="true"
        className="mt-5 inline-flex items-center gap-1.5 text-xs font-medium text-primaryColor"
      >
        {t('actions.startFreeTrial', 'Start free trial')}
        <FontAwesomeIcon
          icon={faArrowRight}
          className="text-[10px] transition-transform group-hover:translate-x-0.5"
        />
      </span>
    </button>
  );
};

CompanyTypeCard.propTypes = {
  type: PropTypes.oneOf(['declarant', 'operator']).isRequired,
  title: PropTypes.string.isRequired,
  icon: PropTypes.object.isRequired,
  description: PropTypes.string.isRequired,
  highlights: PropTypes.arrayOf(PropTypes.string),
  onClick: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

CompanyTypeCard.defaultProps = {
  highlights: [],
  isLoading: false,
};

export default CompanyTypeCard;
